/**
 * Normalize for case-insensitive comparison
 */
export const normalize = (value?: string) =>
  value ? String(value).trim().toLowerCase() : '';

/**
 * Extracts the leading number from a string (e.g. "13-16 feet" → 13, "8'" → 8).
 * Returns Infinity for strings with no leading number, so they sort to the end.
 */
const extractLeadingNumber = (s: string) => {
  const match = String(s).match(/^\s*(\d+(?:\.\d+)?)/);
  if (!match) return Infinity;

  const value = parseFloat(match[1]);
  return Number.isNaN(value) ? Infinity : value;
};

/**
 * Business-defined Top options
 */
export const TOP_OPTIONS: Record<string, string[]> = {
  // Metal finishes
  finish: [
    'Standard White:#eeeee2',
    'Standard Black:#000000',
    'Brushed Stainless:#a8a9ad', // WEB-2590
    'Standard Silver:#afb0ad',
    'Graphite Gray:#555651',
    'Polished Silver:#b0b1ad',
  ],

  // Wood finishes
  finish_wood: [
    'Unfinished:#FFFFFF',
    'Natural:#DFC098',
    'Dune:#C3AD95',
    'Wheatfield:#B58452',
    'Willow:#D1A280'
  ],
};

/**
 * Business-defined Remaining options (Metal only for now)
 */
export const REMAINING_OPTIONS: Record<string, string[]> = {
  finish: [
    'Bright White:#f0f1ed',
    'Textured Plaster:#f7f3e9',
    'Alabaster White:#ddd6bf',
    'Limestone Buff:#c3b9a5',
    'Desert Sand:#d4bd90',
    'Burnished Gold:#897c57',
    'Bright Brass:#b08d61',
    'Warm Stone:#877d6a',
    'Iron Oxide:#7c5d46',
    'Pistachio Green:#7a8965',
    'Spruce Green:#506356',
    'Midnight Blue:#46505c',
    'Gray Mist:#6c6d66',
    'Light Bronze:#5b5146',
    'Burnished Bronze:#3f3529',
    'Textured Black:#1e1e1e',
    'Deep Pewter:#3c3a38',
    'Jet Black:#1e1e1e',
  ],
};

/**
 * Returns ordered options based on:
 *
 * Top options (business defined)
 * Remaining options (business defined)
 * Any unmatched backend options (original backend order)
 *
 * ✔ Never drops backend values
 * ✔ Deduplicated
 * ✔ Case-insensitive match
 * ✔ Preserves backend casing
 */
export const getBusinessOrderedOptions = <T extends string | { name: string; color?: string }>(
  attribute: string,
  options: T[],
  productType?: string,
): T[] => {
  const key =
    productType === 'wood'
      ? `${attribute}_wood`
      : attribute;
  const topOptions = TOP_OPTIONS[key] || [];
  const remainingOptions = REMAINING_OPTIONS[key] || [];

  // Helper to extract just the name part from Name:#hex or just Name
  const getNameOnly = (val: T) => {
    if (typeof val === 'string') return normalize(val).split(':')[0].trim();
    return normalize((val as { name: string }).name).split(':')[0].trim();
  };

  const normalizedBackendNames = options.map(getNameOnly);
  const seen = new Set<string>();
  const result: T[] = [];

  const addIfExists = (value: string) => {
    const targetName = normalize(value).split(':')[0].trim();
    // Find the first matching option in the backend options
    // that we haven't already added.
    const index = normalizedBackendNames.findIndex((backendName, idx) =>
      backendName === targetName && !seen.has(normalizedBackendNames[idx])
    );

    if (index !== -1) {
      result.push(options[index]); // preserve backend casing and full string (with/without hex)
      seen.add(normalizedBackendNames[index]);
    }
  };

  // Add top options
  topOptions.forEach(addIfExists);

  // Add remaining options
  remainingOptions.forEach(addIfExists);

  // Append unmatched backend options
  options.forEach((option, index) => {
    const backendName = normalizedBackendNames[index];
    if (!seen.has(backendName)) {
      result.push(option);
      seen.add(backendName);
    }
  });

  // If there are no business-defined options and all values start with a number,
  // sort numerically ascending (e.g. "3'", "6'", "8'", "12'" or "3-6 feet", "13-16 feet").
  if (topOptions.length === 0 && remainingOptions.length === 0 && result.length > 0) {
    const getString = (v: T): string => (typeof v === 'string' ? v : (v as { name: string }).name);
    const allNumeric = result.every((v) => extractLeadingNumber(getString(v)) !== Infinity);
    if (allNumeric) {
      result.sort((a, b) => extractLeadingNumber(getString(a)) - extractLeadingNumber(getString(b)));
    }
  }

  return result;
};

export const MAX_VISIBLE_OPTIONS = 5;
